import { AppAction } from './enum';
import {
    AppError,
    AppState,
} from './interface';
import {
    JarvisAvcSuccessResponse,
    ContainerActions,
    JarvisProduct,
    CustomerInfo,
    JarvisAlternativeAvailability,
} from '../../interface';
import { CustomerType } from '@vfde-sails/constants';
import { Address } from '@vfde-sails/avc-core';

/**
 * Loads initial state and triggers init sagas
 */
export const initStore = (state: Partial<AppState>) => ({
    type: AppAction.INIT_STORE,
    state,
} as const);

/**
 * Starts the loading state for id
 */
export const startLoading = (id: ContainerActions) => ({
    type: AppAction.START_LOADING,
    id,
} as const);

/**
 * Stops the loading state for id
 */
export const stopLoading = (id: ContainerActions) => ({
    type: AppAction.STOP_LOADING,
    id,
} as const);

/**
 * Sets error
 */
export const setError = (error: AppError) => ({
    type: AppAction.SET_ERROR,
    error,
} as const);

/**
 * Deletes address and voids availability result
 */
export const deleteAddress = () => ({
    type: AppAction.DELETE_ADDRESS,
} as const);

/**
 * Resets availability result
 */
export const resetState = () => ({
    type: AppAction.RESET_STATE,
} as const);

/**
 * Sets initially loaded Jarvis tariffs, needed when user deletes address
 */
export const setInitialTariffs = (tariffs: JarvisProduct[]) => ({
    type: AppAction.SET_INITIAL_TARIFFS,
    tariffs,
} as const);

/**
 * Sets selected tariff id
 */
export const setSelectedTariffId = (selectedTariffId: string) => ({
    type: AppAction.SET_SELECTED_TARIFF_ID,
    selectedTariffId,
} as const);

/**
 * Sets user's TV selection
 */
export const setTVProductSelection = (isTVProductSelected: boolean) => ({
    type: AppAction.SET_TV_PRODUCT_SELECTION,
    isTVProductSelected,
} as const);

/**
 * Sets if user has Friends&Family
 */
export const setFriendsAndFamily = (isFriendsAndFamily: boolean) => ({
    type: AppAction.SET_FRIENDS_AND_FAMILY,
    isFriendsAndFamily,
} as const);

/**
 * Sets user's gigakombi selection
 */
export const setGigakombi = (isGigakombi: boolean) => ({
    type: AppAction.SET_GIGAKOMBI,
    isGigakombi,
} as const);

/**
 * Sets user's young selection
 */
export const setYoung = (isYoung: boolean) => ({
    type: AppAction.SET_YOUNG,
    isYoung,
} as const);

/**
 * Sets the availability based on Jarvis response
 */
export const setAvailability = (
    availabilityResponse: JarvisAvcSuccessResponse,
    availability: JarvisAlternativeAvailability,
    alternativeTariff?: JarvisProduct,
) => ({
    type: AppAction.SET_AVAILABILITY,
    availabilityResponse,
    availability,
    alternativeTariff,
} as const);

/**
 * Sets the customer information with address individually (existing cable case)
 */
export const setCustomer = (customer: CustomerInfo, address: Address) => ({
    type: AppAction.SET_CUSTOMER,
    customer,
    address,
} as const);

/**
 * Fetches the footnote html
 */
export const loadTvFootnote = () => ({
    type: AppAction.LOAD_TV_FOOTNOTE,
} as const);

/**
 * Sets login status with customer type
 */
export const setLoginStatus = (customerType: CustomerType) => ({
    type: AppAction.SET_LOGIN_STATUS,
    customerType,
} as const);

/**
 * Sets the current banner id
 */
export const setBannerId = (id?: string) => ({
    type: AppAction.SET_BANNER_ID,
    id,
} as const);

/**
 * All Actions
 */
export type Actions = ReturnType<typeof initStore>
    | ReturnType<typeof startLoading>
    | ReturnType<typeof stopLoading>
    | ReturnType<typeof setError>
    | ReturnType<typeof deleteAddress>
    | ReturnType<typeof resetState>
    | ReturnType<typeof setInitialTariffs>
    | ReturnType<typeof setSelectedTariffId>
    | ReturnType<typeof setTVProductSelection>
    | ReturnType<typeof setFriendsAndFamily>
    | ReturnType<typeof setGigakombi>
    | ReturnType<typeof setYoung>
    | ReturnType<typeof setAvailability>
    | ReturnType<typeof setCustomer>
    | ReturnType<typeof loadTvFootnote>
    | ReturnType<typeof setLoginStatus>
    | ReturnType<typeof setBannerId>;
